import { createFileRoute, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';
import AccountSettings from '@/components/Settings/AccountSettings';

const settingsRoute = getRouteApi('/_app/settings');

export const Route = createFileRoute('/_app/settings/account')({
  component: AccountSettingsPage,
});

function AccountSettingsPage() {
  const router = useRouter();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  // Import data mutation
  const importDataMutation = useMutation({
    mutationFn: ({
      file,
      service,
      formKey,
    }: {
      file: File;
      service: string;
      formKey: string;
    }) => settingsService.importData(file, service, formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Export CSV mutation
  const exportCsvMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.exportCsv(formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Email update mutation
  const updateEmailMutation = useMutation({
    mutationFn: ({ email, formKey }: { email: string; formKey: string }) =>
      settingsService.updateEmail(email, formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Password update mutation
  const updatePasswordMutation = useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
      confirmPassword,
      formKey,
    }: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
      formKey: string;
    }) =>
      settingsService.updatePassword(
        currentPassword,
        newPassword,
        confirmPassword,
        formKey,
      ),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.deleteAccount(formKey),
    onSuccess: () => {
      // Account deletion will redirect, so we don't need to do anything here
      // Flash message will be handled by useFlashMessages hook if backend sets one
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  return (
    <AccountSettings
      user={settings.user}
      onImport={(file: File, service: string) => {
        importDataMutation.mutate({
          file,
          service,
          formKey,
        });
      }}
      isImporting={importDataMutation.isPending}
      onExportCsv={() => {
        exportCsvMutation.mutate(formKey);
      }}
      isExporting={exportCsvMutation.isPending}
      onEmailUpdate={(newEmail: string) => {
        updateEmailMutation.mutate({
          email: newEmail,
          formKey,
        });
      }}
      isUpdatingEmail={updateEmailMutation.isPending}
      onPasswordUpdate={(
        currentPassword: string,
        newPassword: string,
        confirmPassword: string,
      ) => {
        updatePasswordMutation.mutate({
          currentPassword,
          newPassword,
          confirmPassword,
          formKey,
        });
      }}
      isUpdatingPassword={updatePasswordMutation.isPending}
      onDeleteAccount={() => {
        deleteAccountMutation.mutate(formKey);
      }}
      isDeletingAccount={deleteAccountMutation.isPending}
    />
  );
}
